import React, { forwardRef, useState, useEffect } from "react";
import { __ } from "@wordpress/i18n";
import {
	CheckboxControl,
	ToggleControl,
	Spinner,
	Notice,
	Button,
} from "@wordpress/components";
import { getImageHubSettings } from "../../context/useSettings";

const ajaxUrl = image_hub_data.ajaxurl;
const nonce = image_hub_data.nonce;

const DEFAULT_STATE = {
	image_hub_enable_unsplash: true,
	image_hub_enable_openverse: true,
	image_hub_enable_pixabay: true,
	image_hub_enable_pexels: true,
	image_hub_enable_giphy: true,
	image_hub_enable_image_attribution: true,
	image_hub_enable_media_modal: true,
}

const UnforwardedGeneral = (props, ref) => {
	// Single settings state object with all settings
	const [settings, setSettings] = useState(DEFAULT_STATE);
	const [originalSettings, setOriginalSettings] = useState(DEFAULT_STATE);
	const hasSettingsChanged = () => {
		return Object.keys(DEFAULT_STATE).some(key => settings[key] !== originalSettings[key]);
	};
	const [isSaving, setIsSaving] = useState(false);
	const [saveMessage, setSaveMessage] = useState("");
	const [saveStatus, setSaveStatus] = useState("");
	function countTrueExcludingImageAndMedia(obj) {
		return Object.entries(obj).reduce((count, [key, value]) => {
			if (value === true && !key.includes("attribution") && !key.includes("media")) {
				count++;
			}
			return count;
		}, 0);
	}

	// Provider metadata
	const providers = [
		{
			id: "image_hub_enable_unsplash",
			label: "Unsplash.com",
			description: __(
				"High-quality stock images from professional photographers.",
			),
		},
		{
			id: "image_hub_enable_openverse",
			label: "Openverse",
			description: __(
				"A large library of openly licensed images from various sources.",
			),
		},
		{
			id: "image_hub_enable_pixabay",
			label: "Pixabay.com",
			description: __(
				"Free images, videos, and graphics for commercial use.",
			),
		},
		{
			id: "image_hub_enable_pexels",
			label: "Pexels.com",
			description: __(
				"Curated free stock photos and videos from creators.",
			),
		},
		{
			id: "image_hub_enable_giphy",
			label: "Giphy",
			description: __(
				"Search and insert GIFs directly into your media library.",
			),
		},
	];

	const { imageHubSettings, isLoading } = getImageHubSettings();

	useEffect(() => {
		const updatedSettings = { ...settings };
		Object.keys(updatedSettings).forEach((key) => {
			updatedSettings[key] = !!imageHubSettings[key];
		});
		setOriginalSettings(updatedSettings)
		setSettings(updatedSettings);
	}, [imageHubSettings]);

	// Generic handler for any setting change
	const handleSettingChange = (key, value) => {
		setSettings((prev) => ({
			...prev,
			[key]: value,
		}));
	};

	// Save settings to WordPress
	const saveSettings = () => {
		const activeProvidersCount = providers.filter(provider => settings[provider.id]).length;

		const isAtLeastTwoEnabled = activeProvidersCount >= 1;
		if (!isAtLeastTwoEnabled) {
			setSaveMessage(__("At least one provider must be selected!", "image-hub"));
			setSaveStatus("error");

			// Clear success message after 3 seconds
			setTimeout(() => {
				setSaveMessage("");
				setSaveStatus("");
			}, 3000);
			return
		}
		setIsSaving(true);

		fetch(ajaxUrl, {
			method: "POST",
			body: new URLSearchParams({
				action: "image_hub_update_settings",
				nonce: nonce,
				data:JSON.stringify( settings )
			}),
		})
			.then((response) => {
				setIsSaving(false);
				setSaveMessage(__("Settings saved successfully!", "image-hub"));
				setSaveStatus("success");
				setOriginalSettings(settings)

				// Clear success message after 3 seconds
				setTimeout(() => {
					setSaveMessage("");
					setSaveStatus("");
				}, 3000);
			})
			.catch((error) => {
				console.error("Error saving settings:", error);
				setIsSaving(false);
				setSaveMessage(__("Failed to save settings.", "image-hub"));
				setSaveStatus("error");
			});
	};

	// Show loading spinner while loading
	if (isLoading) {
		return (
			<div className="image-hub-settings-loading">
				<Spinner />
				<p>{__("Loading settings...", "image-hub")}</p>
			</div>
		);
	}

	return (
		<div ref={ref} className="image-hub-settings-general-container">

			<h1>{__("Image Providers","image-hub")}</h1>
			<h4>
				{__(
					"Select one or more sources for image search and downloads.",
				)}
			</h4>
			{providers.map((provider) => (
				<div key={provider.id} style={{ marginBottom: "10px" }}>
					<CheckboxControl
						__nextHasNoMarginBottom
						className={"image-hub-settings-general-checkbox " + ((settings[provider.id] && (countTrueExcludingImageAndMedia(settings) <= 1)) ? "image-hub-settings-general-checkbox-disabled" : "")}
						label={provider.label}
						checked={settings[provider.id]}
						disabled={settings[provider.id] && countTrueExcludingImageAndMedia(settings) <= 1}
						onChange={() => {

							handleSettingChange(
								provider.id,
								!settings[provider.id],
							)
						}
						}
					/>
					<p
						style={{
							marginLeft: "25px",
							fontSize: "0.9em",
							color: "#555",
						}}
					>
						{provider.description}
					</p>
				</div>
			))}
			<br /> <br />
			<ToggleControl
				__nextHasNoMarginBottom
				label={__("Image Attribution","image-hub")}
				help={__("Automatically add image attribution as captions.","image-hub")}
				checked={settings.image_hub_enable_image_attribution}
				onChange={() =>
					handleSettingChange(
						"image_hub_enable_image_attribution",
						!settings.image_hub_enable_image_attribution,
					)
				}
			/>
			<br />
			<ToggleControl
				__nextHasNoMarginBottom
				label={__('Enable "Image Hub" tab in the media modal',"image-hub")}
				help={__(
					"Adds a tab in the media uploader for quick image search and insertion.",
				)}
				checked={settings.image_hub_enable_media_modal}
				onChange={() =>
					handleSettingChange(
						"image_hub_enable_media_modal",
						!settings.image_hub_enable_media_modal,
					)
				}
			/>
			<br />
			<div style={{ display: "flex", gap: "10px", alignItems: "baseline", justifyContent: "space-between" }}>

				<Button
					style={{ marginTop: "16px" }}
					variant="primary"
					onClick={saveSettings}
					isBusy={isSaving}
					disabled={isSaving || !hasSettingsChanged()}
				>
					{isSaving
						? __("Saving...", "image-hub")
						: __("Save Settings", "image-hub")}
				</Button>
				<div style={{ width: "90%" }}>
					{saveMessage && (
						<Notice status={saveStatus} isDismissible={false}>
							{saveMessage}
						</Notice>
					)}
				</div>
			</div>
			<br /> <br />
		</div>
	);
};

export const General = forwardRef(UnforwardedGeneral);
